import { externalTooltipHandler } from './standingsGraphTooltipHandler';
import { DateTime } from "luxon";


const IMAGE_POINT_SIZE_PX = 36;
const ALL_STAR_BREAK_REASON = 'All-Star Break';
const LOGO_ASSET_STATE_CHANGE_EVENT = 'basketology:logo-asset-state-change';
const logoImageCache = new Map();
let pendingLogoAssetStateChange = false;


function _notifyLogoAssetStateChange() {
    if (typeof window === 'undefined' || pendingLogoAssetStateChange) {
        return;
    }

    pendingLogoAssetStateChange = true;
    window.requestAnimationFrame(() => {
        pendingLogoAssetStateChange = false;
        window.dispatchEvent(new Event(LOGO_ASSET_STATE_CHANGE_EVENT));
    });
}


function _getTeamLogoPath(logoImagePath, isGrayScale) {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    var imagePath = isGrayScale ? logoImagePath.replace('colour', 'grayscale') : logoImagePath;
    if (!isMobile) {
        imagePath = imagePath.replace('.png', '_small.png');
    }
    return imagePath;
}


function _getCachedLogoImage(imagePath) {
    const cachedLogo = logoImageCache.get(imagePath);
    if (cachedLogo !== undefined) {
        return cachedLogo;
    }

    const teamLogo = new Image(IMAGE_POINT_SIZE_PX, IMAGE_POINT_SIZE_PX);
    teamLogo.style.imageRendering = 'auto';

    const cacheEntry = {
        image: teamLogo,
        status: 'loading',
        promise: null,
    };
    logoImageCache.set(imagePath, cacheEntry);

    cacheEntry.promise = new Promise(resolve => {
        teamLogo.onload = () => {
            cacheEntry.status = 'loaded';
            _notifyLogoAssetStateChange();
            resolve(cacheEntry);
        };
        teamLogo.onerror = () => {
            cacheEntry.status = 'error';
            _notifyLogoAssetStateChange();
            resolve(cacheEntry);
        };
    });
    teamLogo.src = imagePath;

    return cacheEntry;
}


function _getLogoCacheEntryPromise(cacheEntry) {
    if (cacheEntry.status === 'loading' && cacheEntry.promise) {
        return cacheEntry.promise;
    }

    return Promise.resolve(cacheEntry);
}

// Standings graph user options


// TODO split this up so data is separate to presentation options
const standingsGraphTeamOptions = {
    all: 'all',
}

const TEAM_SUBSET_CONFERENCE_ORDER = ['East', 'West'];

function _normalizeTeamSubsetKey(value) {
    return String(value).trim().toLowerCase().replace(/\s+/g, '_');
}

function _sortByPreferredOrderThenAlpha(values, preferredOrder) {
    const preferredIndex = new Map();
    for (let i = 0; i < preferredOrder.length; i++) {
        preferredIndex.set(preferredOrder[i], i);
    }

    return [...values].sort((a, b) => {
        const aIndex = preferredIndex.has(a) ? preferredIndex.get(a) : Infinity;
        const bIndex = preferredIndex.has(b) ? preferredIndex.get(b) : Infinity;

        if (aIndex !== bIndex) {
            return aIndex - bIndex;
        }

        return a.localeCompare(b);
    });
}

function getStandingsGraphTeamOptions(teamData) {
    const teamOptions = {
        all: standingsGraphTeamOptions.all,
    };

    if (!Array.isArray(teamData) || teamData.length === 0) {
        return teamOptions;
    }

    const conferences = new Set();
    const divisions = new Set();
    for (const team of teamData) {
        if (team.conference !== null && team.conference !== undefined && team.conference !== '') {
            conferences.add(team.conference);
        }
        if (team.division !== null && team.division !== undefined && team.division !== '') {
            divisions.add(team.division);
        }
    }

    const sortedConferences = _sortByPreferredOrderThenAlpha(conferences, TEAM_SUBSET_CONFERENCE_ORDER);
    const sortedDivisions = _sortByPreferredOrderThenAlpha(divisions, []);

    if (sortedConferences.length > 0) {
        teamOptions.conferences = null;
        for (const conference of sortedConferences) {
            teamOptions[`conference_${_normalizeTeamSubsetKey(conference)}`] = conference;
        }
    }

    if (sortedDivisions.length > 0) {
        teamOptions.divisions = null;
        for (const division of sortedDivisions) {
            teamOptions[`division_${_normalizeTeamSubsetKey(division)}`] = division;
        }
    }

    return teamOptions;
}

const standingsGraphXAxisTimeScaleOptions = {
    gameNum: 'game',
    day: 'day',
    week: 'week',
    month: 'month',
}

const standingsGraphYAxisOptions = {
    record: 'record',
    seed: 'seed'
}


function getSeasonOptions() {
    const currentDateTime = DateTime.now();
    let currentSeasonYear = currentDateTime.month <= 10 ? currentDateTime.year : currentDateTime.year + 1;
    var seasonOptions = {};
    for (let year = OLDEST_SUPPORTED_SEASON_YEAR; year < currentSeasonYear; year++) {
        seasonOptions[year] = `${year}-${(year + 1).toString().slice(2)}`;
    }
    return seasonOptions;
}


const OLDEST_SUPPORTED_SEASON_YEAR = 1983;
const standingsGraphSeasonOptions = getSeasonOptions();

const dateStringToDateTime = (dateString) => DateTime.fromISO(dateString);
const millisecondsToDateTime = (dateMS) => DateTime.fromMillis(dateMS);
const dateStringToMilliseconds = (dateString) => dateStringToDateTime(dateString).toMillis();


// Manually defined season breaks
const staticSeasonBreaks = {
    '2019-20': [{start: dateStringToMilliseconds('2020-03-11'), end: dateStringToMilliseconds('2020-07-29'), reason: 'COVID-19 Suspension'},],
    '2025-26': [{start: dateStringToMilliseconds('2026-02-14'), end: dateStringToMilliseconds('2026-02-19'), reason: ALL_STAR_BREAK_REASON},],
}

function skipped(ctx, value, onlyIfNotActive=false) {
    if (onlyIfNotActive) {
        let activeElements = ctx.chart.getActiveElements();
        for (const dataset of activeElements) {
            if (dataset.datasetIndex === ctx.datasetIndex) {
                return undefined;
            }
        }
    }
    return ctx.p0.skip || ctx.p1.skip ? value : undefined;
}

// Create a fresh options object for each render/mode switch.
function getStandingsGraphOptionsBase() {
    return {
        interaction: {
            intersect: true,
            mode: 'nearest',
        },
        spanGaps: true,
        segment: {
            // borderColor: ctx => skipped(ctx, 'rgb(0,0,0,0.2)'),
            borderColor: ctx => skipped(ctx, addAlphaToHexColour(ctx.p0.options.borderColor, 0.4), true),
            borderDash: ctx => skipped(ctx, [3, 4]),
            // borderWidth: ctx => skipped(ctx, 2),
        },
        layout: {
            padding: {
                right: IMAGE_POINT_SIZE_PX,
                left: 0,
            }
        },
        scales: {
            x: undefined,
            y: {
                type: 'linear',
                title: {
                    text: '',
                    display: true
                },
                ticks: {
                    count: undefined,
                }
            }
        },
        elements: {
            line: {
                borderWidth: 5,
                borderCapStyle: 'round'
            },
            point: {
                borderWidth: 0,
            },
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: false,
                position: 'nearest',
                external: externalTooltipHandler,
            },
            autocolors: false,
            annotation: {
                annotations: {
                    playoffLine: {
                        type: 'line',
                        display: false,
                        yMin: 6.5,
                        yMax: 6.5,
                        borderColor: 'rgb(132, 99, 255)',
                        borderWidth: 2,
                        borderDash: [10, 10],
                        drawTime: 'beforeDatasetsDraw',
                        label: {
                            content: 'Playoff',
                            display: true,
                            backgroundColor: 'rgba(0,0,0,0)',
                            color: 'rgb(132, 99, 255)'
                        }
                    },
                    playinLine: {
                        type: 'line',
                        display: false,
                        yMin: 10.5,
                        yMax: 10.5,
                        borderColor: 'rgb(255, 99, 132)',
                        borderWidth: 2,
                        borderDash: [10, 10],
                        label: {
                            content: 'Play-in',
                            display: true,
                        }
                    }
                }
            }
        },
        onHover: onHoverHandler,
        maintainAspectRatio: false,
    };
}


const teamGraphDatasetBase = {
    label: "",
    data: [],
    clip: {left: false, top: false, right: IMAGE_POINT_SIZE_PX, bottom: false},
    hidden: false,
    pointHitRadius: 10,
}


function _insertSeasonBreakData(data, seasonOption, allStarBreak) {
    const staticBreaks = staticSeasonBreaks[seasonOption];

    let maxPointDateMs = 0;
    for (const d of data) {
        if (d.x > maxPointDateMs) {
            maxPointDateMs = d.x;
        }
    }

    const breaks = staticBreaks === undefined ? [] : [...staticBreaks];
    if (allStarBreak !== undefined && allStarBreak.start !== null && allStarBreak.start !== undefined && allStarBreak.end !== null && allStarBreak.end !== undefined) {
        breaks.push({start: allStarBreak.start, end: allStarBreak.end, reason: ALL_STAR_BREAK_REASON});
    }

    for (const brk of breaks) {
        // Bisect the dataset to find the points either side of the break
        let breakStartMS = brk.start;
        let breakEndMS = brk.end;
        let lastPointBeforeBreakIdx = null;
        let firstPointAfterBreakIdx = null;
        for (let i = 0; i < data.length; i++) {
            if (data[i].x < breakStartMS) {
                lastPointBeforeBreakIdx = i;
            } else if (data[i].x > breakEndMS) {
                firstPointAfterBreakIdx = i;
                break;
            }
        }
        if (lastPointBeforeBreakIdx === null || firstPointAfterBreakIdx === null) {
            continue;
        }

        // Interpolate to find the datapoints to insert either side of the break
        let lastPointBeforeBreak = data[lastPointBeforeBreakIdx];
        let firstPointAfterBreak = data[firstPointAfterBreakIdx];
        // let preBreakY = lastPointBeforeBreak.y + slope * preBreakXDist;
        // let postBreakY = firstPointAfterBreak.y - slope * postBreakXDist;
        let yValue = (lastPointBeforeBreak.y + firstPointAfterBreak.y) / 2;
        let breakPoints = [
            {x: breakStartMS - 1, y: yValue, interactive: false},  // Interpolated point just before break
            {x: breakStartMS, y: null},                               // Break start
            {x: breakEndMS, y: null},                                 // Break end
            {x: breakEndMS + 1, y: yValue, interactive: false}    // Interpolated point just after break
        ];

        // Insert the break points, deleting data which falls over the break
        let deleteCount = firstPointAfterBreakIdx - lastPointBeforeBreakIdx - 1;
        data.splice(lastPointBeforeBreakIdx + 1, deleteCount, ...breakPoints);
    }

    return data
}


function _getRecordDataForTeamByGameNum(team) {
    const zeroDataPoint = {x: 0, y: 0};
    var data = [zeroDataPoint];
    for (const game of team.games) {
        let x = game.game_num;
        let y = game.cumulative_wins - game.cumulative_losses;
        data.push({x: x, y: y, games: [game]});
    }
    return data;
}


function _getRecordDataForTeamByDay(team, earliestGameDateTime) {
    const zeroDataPoint = {x: earliestGameDateTime.minus({days: 1}).toMillis(), y: 0};
    var data = [zeroDataPoint];
    for (const game of team.games) {
        let x = game.date_ms;
        let y = game.cumulative_wins - game.cumulative_losses;
        data.push({x: x, y: y, games: [game]});
    }
    return data;
}


function _getSeedDataForTeamByDay(team, earliestGameDateTime, teamSubsetOption) {
    const zeroDataPoint = {x: earliestGameDateTime.minus({'days': 1}).toMillis(), y: teamSubsetOption === standingsGraphTeamOptions.all ? 30 / 2 : 15 / 2};
    var data = [zeroDataPoint];
    const rankOverTime = teamSubsetOption === standingsGraphTeamOptions.all ? team.league_rank_by_date : team.conference_seed_by_date;
    for (const [dateMS, rank] of Object.entries(rankOverTime)) {
        let x = Number(dateMS);
        let y = rank;
        data.push({x: x, y: y});
    }
    var uniqueDataPoints = [];
    for (var i = 0; i < data.length; i++) {
        if (i === 0 || i === data.length - 1) {
            uniqueDataPoints.push(data[i]);
            continue;
        }
        const [last, current, next] = data.slice(i - 1, i + 2);
        if (last.y !== current.y || current.y !== next.y) {
            uniqueDataPoints.push(current);
        }
    }
    return uniqueDataPoints;
}


function _getRecordDataForTeamByTimeStep(team, earliestGameDateTime, timeStepOption) {
    const zeroDataPoint = {x: earliestGameDateTime.toMillis(), y: 0};
    const timeStepString = timeStepOption === standingsGraphXAxisTimeScaleOptions.week ? 'week' : 'month';
    var data = [zeroDataPoint];

    var maxGameDateMS = earliestGameDateTime.toMillis();
    for (const game of team.games) {
        if (game.date_ms > maxGameDateMS) {
            maxGameDateMS = game.date_ms;
        }
    }

    // Bin games by time step
    var gamesByTimeStep = new Map();
    for (const game of team.games) {
        let gameDateTime = millisecondsToDateTime(game.date_ms);
        let offset = {};
        offset[timeStepString] = 1;
        let timeStepEndDateTime = gameDateTime.plus(offset).startOf(timeStepString);
        var timeStepMS = timeStepEndDateTime.toMillis();
        timeStepMS = Math.min(timeStepMS, maxGameDateMS);

        if (gamesByTimeStep.has(timeStepMS)) {
            gamesByTimeStep.get(timeStepMS).push(game);
        } else {
            gamesByTimeStep.set(timeStepMS, [game]);
        }
    }

    // Convert bins of games to data points
    for (const [timeStepMS, games] of gamesByTimeStep) {
        if (games.length === 0) {
            continue;
        }
        games.sort((a, b) => a.date_ms - b.date_ms);
        var latestGameInTimeStep = games[games.length - 1];
        let x = timeStepMS;
        let y = latestGameInTimeStep.cumulative_wins - latestGameInTimeStep.cumulative_losses;
        data.push({x: x, y: y, games: games});
    }
    return data;
}


function _getSeedDataForTeamByTimeStep(team, earliestGameDateTime, timeStepOption, teamSubsetOption) {
    const zeroDataPoint = {x: earliestGameDateTime.toMillis(), y: teamSubsetOption === standingsGraphTeamOptions.all ? 30 / 2 : 15 / 2};
    const timeStepString = timeStepOption === standingsGraphXAxisTimeScaleOptions.week ? 'week' : 'month';
    const rankOverTime = teamSubsetOption === standingsGraphTeamOptions.all ? team.league_rank_by_date : team.conference_seed_by_date;
    var data = [zeroDataPoint];

    var maxSeedDateMS = 0;
    for (const dateMS of Object.keys(rankOverTime)) {
        let intDateMS = Number(dateMS);
        if (intDateMS > maxSeedDateMS) {
            maxSeedDateMS = intDateMS;
        }
    }

    // Bin seed data by time intervals
    var seedsByTimeStep = new Map();
    for (const [dateMS, rank] of Object.entries(rankOverTime)) {
        let dateTime = millisecondsToDateTime(Number(dateMS));
        let offset = {};
        offset[timeStepString] = 1;
        let timeStepEndDateTime = dateTime.plus(offset).startOf(timeStepString);
        var timeStepMS = timeStepEndDateTime.toMillis();
        timeStepMS = Math.min(timeStepMS, maxSeedDateMS);

        if (seedsByTimeStep.has(timeStepMS)) {
            seedsByTimeStep.get(timeStepMS).push(rank);
        } else {
            seedsByTimeStep.set(timeStepMS, [rank]);
        }
    }

    // Convert bins of seed data to graph data points
    for (const [timeStepMS, ranks] of seedsByTimeStep) {
        if (ranks.length === 0) {
            continue;
        }
        let x = timeStepMS;
        let y = ranks[ranks.length - 1];
        data.push({x: x, y: y});
    }
    return data;
}


function _getDataForTeam(team, earliestGameDateTime, xAxisTimeStepOption, yAxisOption, teamSubsetOption) {
    if (xAxisTimeStepOption === standingsGraphXAxisTimeScaleOptions.gameNum) {
        return _getRecordDataForTeamByGameNum(team);
    } else if (xAxisTimeStepOption === standingsGraphXAxisTimeScaleOptions.day) {
        if (yAxisOption === standingsGraphYAxisOptions.record) {
            return _getRecordDataForTeamByDay(team, earliestGameDateTime);
        } else {
            return _getSeedDataForTeamByDay(team, earliestGameDateTime, teamSubsetOption);
        }
    } else {
        if (yAxisOption === standingsGraphYAxisOptions.record) {
            return _getRecordDataForTeamByTimeStep(team, earliestGameDateTime, xAxisTimeStepOption);
        } else {
            return _getSeedDataForTeamByTimeStep(team, earliestGameDateTime, xAxisTimeStepOption, teamSubsetOption);
        }
    }
}


function _getLogoImageForTeamName(logoImagePath, isGrayScale) {
    const imagePath = _getTeamLogoPath(logoImagePath, isGrayScale);
    const cachedLogo = _getCachedLogoImage(imagePath);
    if (cachedLogo.status !== 'loaded') {
        return 'circle';
    }

    return cachedLogo.image;
}


function _setDatasetPointStyling(dataset, active) {
    if (!dataset.data) {
        return;
    }
    var lastNotNullIdx = 0;
    const nullIndxs = [];
    for (let i = 0; i < dataset.data.length; i++) {
        let dataPoint = dataset.data[i];
        if (dataPoint.y !== null && dataPoint.y !== undefined && dataPoint.interactive !== false) {
            lastNotNullIdx = i;
        } else {
            nullIndxs.push(i);
        }
    }
    var pointStyles = new Array(dataset.data.length).fill('point');
    pointStyles[lastNotNullIdx] = _getLogoImageForTeamName(dataset.logoURL, !active);
    dataset.pointStyle = pointStyles;

    var pointRadii = new Array(dataset.data.length).fill(2.5);
    for (const i of nullIndxs) {
        pointRadii[i] = 0;
    }
    pointRadii[0] = 0;
    dataset.pointRadius = pointRadii;

    var hoverRadii = new Array(dataset.data.length).fill(5);
    for (const i of nullIndxs) {
        hoverRadii[i] = 0;
    }
    hoverRadii[0] = 0;
    hoverRadii[lastNotNullIdx] = IMAGE_POINT_SIZE_PX;
    dataset.pointHoverRadius = hoverRadii;
}


function _getBrandingForTeam(teamId, teamHistories, seasonOption) {
    var teamBrandingHistory = teamHistories[teamId]["branding_history"];
    var currentSeason = Number(seasonOption.split('-')[0]);
    for (const brandEra of teamBrandingHistory) {
        if (currentSeason >= brandEra.from && (brandEra.to === null || brandEra.to === undefined || currentSeason <= brandEra.to)) {
            return brandEra;
        }
    }
    return null;
}


function preloadStandingsLogoAssets(teamData, teamHistories, teamSubsetOption, seasonOption) {
    if (teamData === undefined || teamHistories === null || teamHistories === undefined) {
        return Promise.resolve();
    }

    const preloadPromises = [];
    for (const team of teamData) {
        if (!teamBelongsToTeamSubset(team, teamSubsetOption)) {
            continue;
        }

        const teamBranding = _getBrandingForTeam(team.id, teamHistories, seasonOption);
        if (!teamBranding) {
            continue;
        }

        for (const isGrayScale of [false, true]) {
            const imagePath = _getTeamLogoPath(teamBranding.logo, isGrayScale);
            const cacheEntry = _getCachedLogoImage(imagePath);
            preloadPromises.push(_getLogoCacheEntryPromise(cacheEntry));
        }
    }

    return Promise.allSettled(preloadPromises);
}

function _getDatasetForTeamData(team, teamHistories, data, xAxisTimeStepOption, yAxisOption, teamSubsetOption, seasonOption) {
    const teamBranding = _getBrandingForTeam(team.id, teamHistories, seasonOption);
    var dataset = {
        ...teamGraphDatasetBase,
        label: team.name,
        data: data,
        primaryColour: teamBranding.primary_colour,
        secondaryColour: teamBranding.secondary_colour,
        textColour: teamBranding.text_colour,

        backgroundColor: teamBranding.secondary_colour,
        borderColor: teamBranding.primary_colour,
        order: team.league_rank,
        tension: yAxisOption === standingsGraphYAxisOptions.seed ? 0.1 : 0.3,

        // Custom attributes
        yAxisOption: yAxisOption,
        xAxisTimeStepOption: xAxisTimeStepOption,
        teamSubsetOption: teamSubsetOption,
        team: team,
        logoURL: teamBranding.logo,
        champions: seasonOption in teamHistories[team.id].championships,
        championshipOppTeam: teamHistories[team.id].championships[seasonOption],
    }

    _setDatasetPointStyling(dataset, true);
    return dataset;
}


function _getDataForTeams(teamData, teamHistories, allStarBreak, earliestGameDateTime, xAxisTimeStepOption, yAxisTypeOption, teamSubsetOption, seasonOption) {
    var datasets = [];
    for (let teamIdx = 0; teamIdx < teamData.length; teamIdx++) {
        var team = teamData[teamIdx];
        var teamBranding = _getBrandingForTeam(team.id, teamHistories, seasonOption);
        if (!teamBranding) {
            continue;
        }
        var data = _getDataForTeam(team, earliestGameDateTime, xAxisTimeStepOption, yAxisTypeOption, teamSubsetOption);
        var dataWithBreaks = _insertSeasonBreakData(data, seasonOption, allStarBreak);
        var teamDataset = _getDatasetForTeamData(team, teamHistories, dataWithBreaks, xAxisTimeStepOption, yAxisTypeOption, teamSubsetOption, seasonOption);
        datasets.push(teamDataset);
    }

    return {datasets: datasets};
}


function teamBelongsToTeamSubset(team, teamSubsetOption) {
    if (teamSubsetOption === standingsGraphTeamOptions.all) {
        return true;
    } else {
        return team.conference === teamSubsetOption || team.division === teamSubsetOption;
    }
}


function getStandingsGraphDataFromSeasonData(seasonData,           // The season JSON data
                                             teamHistories,        // Team branding/history metadata
                                             teamSubsetOption,     // One of standingsGraphTeamOptions
                                             xAxisTimeStepOption,  // One of standingsGraphXAxisTimeScaleOptions
                                             yAxisTypeOption,      // One of standingsGraphYAxisOptions
                                             seasonOption)         // One of standingsGraphSeasonOptions
{
    var teamData = seasonData.team_data;
    const allStarBreak = seasonData.all_star_break;
    if (teamData === undefined || teamHistories === null || teamHistories === undefined) {
        return {labels: [], datasets: []};
    }

    // Compute the highest number of games and earliest game date played by any team
    var maxGamesPlayed = 0;
    var earliestGameDateMS = Infinity;
    for (let teamIdx = 0; teamIdx < teamData.length; teamIdx++) {
        let team = teamData[teamIdx];
        let teamGamesPlayed = team.games.length;
        if (teamGamesPlayed > maxGamesPlayed) {
            maxGamesPlayed = teamGamesPlayed;
        }
        let teamFirstGameDateMS = team.games[0].date_ms;
        if (teamFirstGameDateMS < earliestGameDateMS) {
            earliestGameDateMS = teamFirstGameDateMS;
        }
    }
    var earliestGameDateTime = millisecondsToDateTime(earliestGameDateMS);

    // Filter out teams that don't pertain to the selected team subset option
    let teamDataSubset = [];
    for (let teamIdx = 0; teamIdx < teamData.length; teamIdx++) {
        if (teamBelongsToTeamSubset(teamData[teamIdx], teamSubsetOption)) {
            teamDataSubset.push(teamData[teamIdx]);
        }
    }
    teamData = teamDataSubset;

    return _getDataForTeams(teamData, teamHistories, allStarBreak, earliestGameDateTime, xAxisTimeStepOption, yAxisTypeOption, teamSubsetOption, seasonOption);
}


function defineSeasonBreakAnnotations(options, seasonOption, allStarBreak) {
    const staticBreaks = staticSeasonBreaks[seasonOption];
    const breaks = staticBreaks === undefined ? [] : [...staticBreaks];
    if (allStarBreak !== undefined && allStarBreak.start !== null && allStarBreak.start !== undefined && allStarBreak.end !== null && allStarBreak.end !== undefined) {
        breaks.push({start: allStarBreak.start, end: allStarBreak.end, reason: ALL_STAR_BREAK_REASON});
    }
    var breakAnnotations = {};
    for (const brk of breaks) {
        breakAnnotations[`${brk.reason}Annotation`] = {
            type: 'box',
            display: true,
            xMin: brk.start,
            xMax: brk.end,
            backgroundColor: 'rgb(0,0,0,0.07)',
            borderColor: 'rgb(0,0,0,0.5)',
            borderWidth: 0,
            drawTime: 'beforeDatasetsDraw',
            label: {
                textStrokeWidth: 3,
                textAlign: 'center',
                content: brk.reason,
                display: true,
                backgroundColor: '#292929',
                color: '#F4F4F4',
                rotation: brk.reason === ALL_STAR_BREAK_REASON ? 90 : 0,
                drawTime: 'afterDatasetsDraw',
            }
        }
    }
    options.plugins.annotation.annotations = breakAnnotations;
}

function _calculateScaleRangesFromData(data) {
    var xMin = Infinity;
    var yMin = Infinity;
    var xMax = -Infinity;
    var yMax = -Infinity;
    for (const dataset of data.datasets) {
        for (const dataPoint of dataset.data) {
            if (dataPoint.x < xMin) {
                xMin = dataPoint.x;
            } else if (dataPoint.x > xMax) {
                xMax = dataPoint.x;
            }
            if (dataPoint.y < yMin) {
                yMin = dataPoint.y;
            } else if (dataPoint.y > yMax) {
                yMax = dataPoint.y;
            }
        }
    }
    return {'xMin': xMin, 'xMax': xMax, 'yMin': yMin, 'yMax': yMax};
}

function getStandingsGraphOptions(data,
                                  allStarBreak,
                                  teamSubsetOption,     // One of standingsGraphTeamOptions
                                  xAxisTimeStepOption,  // One of standingsGraphXAxisTimeScaleOptions
                                  yAxisTypeOption,      // One of standingsGraphYAxisOptions
                                  seasonOption) {
    var options = getStandingsGraphOptionsBase();

    if (xAxisTimeStepOption === standingsGraphXAxisTimeScaleOptions.gameNum) {
        options.scales.x = {
            type: 'linear',
            title: {
                text: 'Game number'
            }
        };
        options.plugins.annotation.annotations = {};
    } else {
        options.scales.x = {
            type: 'time',
            title: {
                text: 'Date'
            },
            time: {
                unit: xAxisTimeStepOption
            }
        };
        defineSeasonBreakAnnotations(options, seasonOption, allStarBreak);
    }

    if (yAxisTypeOption === standingsGraphYAxisOptions.record) {
        let ranges = _calculateScaleRangesFromData(data);
        options.scales.y.reverse = false;
        options.scales.y.title.text = 'Games above .500';
        options.scales.x.min = ranges.xMin;
        options.scales.x.max = ranges.xMax;
        options.scales.y.min = ranges.yMin;
        options.scales.y.max = ranges.yMax;
        options.scales.y.ticks.count = undefined;
        options.scales.y.ticks.autoSkip = true;
    } else {
        // Seed graph
        options.scales.x.title.text = 'Date';
        options.scales.y.reverse = true;
        if (teamSubsetOption === standingsGraphTeamOptions.all) {
            options.scales.y.title.text = 'League-wide rank';
            options.scales.y.min = 1;
            options.scales.y.max = data.datasets.length;
            options.scales.y.ticks.count = data.datasets.length;
            options.scales.y.ticks.autoSkip = false;
        } else {
            options.scales.y.title.text = 'Team seed';
            options.scales.y.min = 1;
            options.scales.y.max = 15;
            options.scales.y.ticks.count = 15;
            options.scales.y.ticks.autoSkip = false;
        }
    }
    return options;
}


function addAlphaToHexColour(colour, alpha) {
    // coerce values between 0 and 1
    var opacity = Math.round(Math.min(Math.max(alpha || 1, 0), 1) * 255);
    return colour + opacity.toString(16).toUpperCase();
}


function onHoverHandler(event, activeElements, chart) {
    var activeIndices = new Set();
    for (let i = 0; i < activeElements.length; i++) {
        activeIndices.add(activeElements[i].datasetIndex);
    }

    if (activeIndices.size > 0) {
        for (let datasetIdx = 0; datasetIdx < chart.data.datasets.length; datasetIdx++) {
            let dataset = chart.data.datasets[datasetIdx]
            if (activeIndices.has(datasetIdx)) {
                _setDatasetPointStyling(dataset, true);
                dataset.backgroundColor = dataset.secondaryColour;
                dataset.borderColor = dataset.primaryColour
                dataset.order = dataset.team.league_rank - 100;
            } else {
                _setDatasetPointStyling(dataset, false);
                dataset.backgroundColor = addAlphaToHexColour("#C9C9C9", 1.0);
                dataset.borderColor = addAlphaToHexColour("#C9C9C9", 1.0);
                dataset.order = dataset.team.league_rank
            }
        }
    } else {
        for (let datasetIdx = 0; datasetIdx < chart.data.datasets.length; datasetIdx++) {
            let dataset = chart.data.datasets[datasetIdx]
            _setDatasetPointStyling(dataset, true);
            dataset.backgroundColor = dataset.secondaryColour;
            dataset.borderColor = dataset.primaryColour;
            dataset.order = dataset.team.league_rank;
        }
    }

    chart.update();
}


function getDefaultTimeScaleForSeason(seasonYear) {
    // Get the current datetime and determine the best timescale to view season
    const currentDateTime = DateTime.now();
    const month = currentDateTime.month;
    const selectedYear = Number(seasonYear.split('-')[0]);
    if (currentDateTime >= DateTime.fromObject({year: selectedYear + 1, month: 10})) {
        // Past season: default to month scale
        return standingsGraphXAxisTimeScaleOptions.month;
    }

    if (month >= 10 && month < 12) {
        // Early season: view by day
        return standingsGraphXAxisTimeScaleOptions.day;
    } else if (month === 12 || (month >= 1 && month < 4)) {
        // Mid season: view by week
        return standingsGraphXAxisTimeScaleOptions.week;
    } else {
        // Late season: view by month
        return standingsGraphXAxisTimeScaleOptions.month;
    }
}


function getDefaultSeason() {
    // Get the current datetime and determine the current season
    const currentDateTime = DateTime.now();
    let year = currentDateTime.month <= 10 ? currentDateTime.year - 1 : currentDateTime.year;
    return standingsGraphSeasonOptions[year];
}


export {
    LOGO_ASSET_STATE_CHANGE_EVENT,
    standingsGraphTeamOptions,
    getStandingsGraphTeamOptions,
    standingsGraphXAxisTimeScaleOptions,
    standingsGraphYAxisOptions,
    standingsGraphSeasonOptions,
    getStandingsGraphOptionsBase,
    getStandingsGraphDataFromSeasonData,
    getStandingsGraphOptions,
    getDefaultTimeScaleForSeason,
    getDefaultSeason,
    preloadStandingsLogoAssets,
    teamBelongsToTeamSubset
};
